import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React from "react";
import { get } from "lodash";

interface Props {
  ad: any;
  result: any;
  scoring: boolean;
}

function scoreColor(score: number): string {
  if (score >= 7) return "#4caf50";
  if (score >= 5) return "#ff9800";
  return "#f44336";
}

function DimensionBar({
  label,
  score,
  comparison,
}: {
  label: string;
  score: number;
  comparison?: string;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" sx={{ color: scoreColor(score) }}>
          {score}/10
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={score * 10}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: "grey.800",
          "& .MuiLinearProgress-bar": {
            bgcolor: scoreColor(score),
            borderRadius: 4,
          },
        }}
      />
      {comparison && (
        <Typography variant="caption" color="text.secondary">
          {comparison}
        </Typography>
      )}
    </Box>
  );
}

function ReferenceCard({
  label,
  refData,
}: {
  label: string;
  refData: any;
}) {
  if (!refData) return null;
  const advertiser = refData.advertiser || "Unknown";
  const ctr = refData.ctr || 0;
  const impressions = refData.impressions || 0;
  const clicks = refData.clicks || 0;
  const rawPreviewUrl = refData.previewUrl || "";
  const previewUrl = rawPreviewUrl
    ? `/copilot/genesys/adeval/proxyimage?url=${encodeURIComponent(rawPreviewUrl)}`
    : "";
  const creativeid = refData.creativeid || "";
  const vertical = refData.vertical || "";
  const size = refData.size || "";
  const refScore = refData.score;
  const startDate = refData.startDate || "";
  const endDate = refData.endDate || "";

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.100" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: "bold" }}>
          {label}
        </Typography>
        {refScore != null && (
          <Chip
            label={`${refScore}/10`}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: refScore >= 7 ? "#4caf50" : refScore >= 5 ? "#ff9800" : "#f44336",
              color: "#fff",
            }}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.primary" }}>
        {advertiser}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {vertical} | {size}
      </Typography>
      <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="caption" color="text.primary">
          <strong>CTR:</strong> {ctr}%
        </Typography>
        <Typography variant="caption" color="text.primary">
          <strong>Impressions:</strong> {impressions.toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.primary">
          <strong>Clicks:</strong> {clicks.toLocaleString()}
        </Typography>
      </Box>
      {startDate && endDate && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          <strong>Ran:</strong> {startDate} to {endDate}
        </Typography>
      )}
      {previewUrl && (
        <Box sx={{ mt: 1 }}>
          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={previewUrl}
              alt={advertiser}
              style={{
                maxWidth: "100%",
                maxHeight: 200,
                borderRadius: 4,
                objectFit: "contain",
              }}
              onError={(e: any) => {
                e.target.style.display = "none";
              }}
            />
          </a>
          <Typography variant="caption" display="block" color="text.secondary">
            Creative ID: {creativeid}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

const AdevalResult = ({ ad, result, scoring }: Props) => {
  if (scoring) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>
          Scoring <strong>{ad.campaignName}</strong> against reference
          library...
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This takes 10-20 seconds
        </Typography>
      </Box>
    );
  }

  if (!result) return null;

  const evaluation = get(result, "evaluation", {});
  const overallScore = get(evaluation, "score", 0);
  const passed = overallScore >= 7;
  const checks = get(evaluation, "checks", {});
  const observed = get(evaluation, "observed", {});
  const strengths = get(evaluation, "strengths", []);
  const improvements = get(evaluation, "improvements", []);
  const summary = get(evaluation, "summary", "");
  const bestMatch = get(result, "bestMatch", null);
  const aspirational = get(result, "aspirational", null);

  const checkDimensions = Object.entries(checks).map(
    ([key, val]: [string, any]) => {
      const s = val?.score || 0;
      return {
        label: key
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c: string) => c.toUpperCase()),
        score: s,
        comparison: val?.comparison || val?.issues?.join(", ") || "",
      };
    }
  );
  checkDimensions.sort((a, b) => b.score - a.score);

  return (
    <Grid container spacing={3}>
      {/* Left: Ad + Overall Score */}
      <Grid item xs={12} md={5}>
        <Card sx={{ mb: 2 }}>
          {ad.bgImageUrl && (
            <CardMedia
              component="img"
              image={ad.bgImageUrl}
              alt={ad.campaignName}
              sx={{ maxHeight: 300, objectFit: "contain" }}
            />
          )}
        </Card>

        <Paper sx={{ p: 2, mb: 2, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            {ad.campaignName}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              color: scoreColor(overallScore),
              fontWeight: "bold",
            }}
          >
            {overallScore}
            <Typography
              component="span"
              variant="h5"
              color="text.secondary"
            >
              /10
            </Typography>
          </Typography>
          <Chip
            label={passed ? "PASS" : "FAIL"}
            color={passed ? "success" : "error"}
            sx={{ mt: 1, fontSize: "1rem", px: 2 }}
          />
          {summary && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              {summary}
            </Typography>
          )}
        </Paper>

        {/* Extracted text from ad */}
        {observed && Object.keys(observed).length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Extracted Text
            </Typography>
            {observed.headline && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Headline:</strong> {observed.headline}
              </Typography>
            )}
            {observed.subheadline && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Subheadline:</strong> {observed.subheadline}
              </Typography>
            )}
            {observed.body_text && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Body:</strong> {observed.body_text}
              </Typography>
            )}
            {observed.cta_text && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>CTA:</strong> {observed.cta_text}
              </Typography>
            )}
            {observed.phone && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Phone:</strong> {observed.phone}
              </Typography>
            )}
            {observed.url && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>URL:</strong> {observed.url}
              </Typography>
            )}
            {observed.imagery_description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Image:</strong> {observed.imagery_description}
              </Typography>
            )}
            {(observed.other_text || []).length > 0 && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Other:</strong> {observed.other_text.join(", ")}
              </Typography>
            )}
            {observed.text_quality && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`Text: ${observed.text_quality}`}
                  size="small"
                  color={
                    observed.text_quality === "clean"
                      ? "success"
                      : observed.text_quality === "minor_issues"
                      ? "warning"
                      : "error"
                  }
                />
                {(observed.text_issues || []).map(
                  (issue: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      display="block"
                      color="warning.main"
                      sx={{ mt: 0.5 }}
                    >
                      {issue}
                    </Typography>
                  )
                )}
              </Box>
            )}
          </Paper>
        )}
      </Grid>

      {/* Right: Dimensions + References */}
      <Grid item xs={12} md={7}>
        {/* Dimension scores */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Scoring Dimensions
          </Typography>
          {checkDimensions.map((dim) => (
            <DimensionBar
              key={dim.label}
              label={dim.label}
              score={dim.score}
              comparison={dim.comparison}
            />
          ))}
        </Paper>

        {/* Strengths & Improvements */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography
                variant="subtitle2"
                color="success.main"
                gutterBottom
              >
                Strengths
              </Typography>
              {strengths.map((s: string, i: number) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  {s}
                </Typography>
              ))}
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography
                variant="subtitle2"
                color="warning.main"
                gutterBottom
              >
                Improvements
              </Typography>
              {improvements.map((s: string, i: number) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  {s}
                </Typography>
              ))}
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Reference Comparisons */}
        <Typography variant="subtitle1" gutterBottom>
          Reference Comparisons
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ReferenceCard label="Best Match" refData={bestMatch} />
          </Grid>
          <Grid item xs={6}>
            <ReferenceCard label="Aspirational" refData={aspirational} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AdevalResult;
